import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
    Signal,
    untracked,
    viewChild,
    WritableSignal
} from "@angular/core";
import {onEach} from '../utils/observable-utils';
import {
    DesktopHomePageLayoutComponent
} from '../page-layout/desktop-home-page-layout/desktop-home-page-layout.component';
import {FullPageLayoutComponent} from '../page-layout/full-page-layout/full-page-layout.component';
import {PageHeaderImageComponent} from '../page-layout/page-header-image/page-header-image.component';
import {ButtonComponent} from "../buttons/button/button.component";
import {ButtonProminence} from "../buttons/button/button";
import {IconButtonComponent} from "../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../buttons/icon-button/icon-button";
import {LinkComponent} from "../buttons/link/link.component";
import {OrderingContext, OrderingContextIntention} from '../ordering-context/ordering-context';
import {
    OrderingContextPanelComponent
} from '../ordering-context/ordering-context-panel/ordering-context-panel.component';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {
    MenuCategorySelectionPanelComponent
} from '../menu/menu-category-selection-panel/menu-category-selection-panel.component';
import {MenuItemData} from '../menu/menu-entity.model';
import {MenuItemSelectionPanelComponent} from '../menu/menu-item-selection-panel/menu-item-selection-panel.component';
import {
    MenuSubcategoryAnchorPanelComponent
} from '../menu/menu-subcategory-anchor-panel/menu-subcategory-anchor-panel.component';
import {MenuCategorySelection, MenuItemSelection, MenuModel} from '../menu/menu.model';
import {GroupOrderPageMode} from '../group-order/group-order-page/group-order-page-mode';
import {
    GroupOrderSummaryIconComponent
} from '../group-order/group-order-summary-icon/group-order-summary-icon.component';
import {GroupOrderModel} from '../group-order/group-order.model';
import {OrderingPhase, SessionModel} from "../session/session.model";
import {ResponsiveDirective} from "../responsive/responsive.directive";
import {PageMode, ResponsiveService} from "../responsive/responsive.service";
import {Page, PageAlignment} from "../navigation/page";
import {AuthService} from "@fedelta-pos/data-client";
import {ContentMessageComponent} from "../notification/message/content-message.component";
import {FooterMessageComponent} from "../notification/message/footer-message.component";
import {MessageDetails, MessageLayout, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {debounceTime, Subject, switchMap, timer} from "rxjs";
import {IdentityModel} from '../identity/identity.model';
import {AppPromoAction, AppPromoData} from '../identity/member-summary-entity.model';
import {LoadingStatus} from '../session/loading-status';
import {ChewzieAttributionComponent} from "../ui-elements/chewzie-attribution/chewzie-attribution.component";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {ReorderStatus} from "./reorder-status";
import {PromoCarouselComponent} from '../promo/promo-carousel/promo-carousel.component';
import {PromoModel} from '../promo/promo.model';
import {DealData, DealProductData} from '../deal/deal-entity.model';
import {DealModel, DealResult, DealResultCode, PendingDealStatus} from '../deal/deal.model';
import {OrderItem, OrderType} from '../order/order';
import {OrderEditPanelComponent} from '../order/order-edit-panel/order-edit-panel.component';
import {OrderErrorsPanelComponent} from '../order/order-errors-panel/order-errors-panel.component';
import {OrderSummaryIconComponent} from '../order/order-summary-icon/order-summary-icon.component';
import {OrderTotalsPanelComponent} from '../order/order-totals-panel/order-totals-panel.component';
import {OrderInitiationStatus, OrderModel, OrderSubmissionStatus} from '../order/order.model';
import {NgClass} from '@angular/common';
import {IntegratedPaymentProvider, PaymentType} from '../payment/payment';
import {PaymentModel} from '../payment/payment.model';
import {QuickPayPanelComponent} from '../payment/quick-pay-panel/quick-pay-panel.component';
import {AppDeviceService} from '../app-device/app-device.service';
import {OrderHeadingPanelComponent} from "./order-heading-panel/order-heading-panel.component";
import {FeatureButtonPanelComponent} from "./feature-button-panel/feature-button-panel.component";
import {SelectorControlComponent} from '../inputs/selector-control/selector-control.component';
import {LegalPanelComponent} from '../legal/legal-panel/legal-panel.component';
import {PageFooterControlDirective} from '../page-layout/page-footer-control.directive';
import {BackgroundImageDirective} from '../ui-elements/image/background-image.directive';
import {AnalyticsService} from '../logging/analytics.service';
import {LiteralPipe} from '../utils/literal.pipe';
import {ConfigurationModel} from '../configuration/configuration.model';
import {effectOnSignalSet} from '../utils/signal-utils';

class ReloadRequest {
    public requestCount: number = 1;
    public deferred: boolean = false;

    constructor(public forceLoadApp: boolean = false,
                public forceLoadMenu: boolean = false,
                public forceInitiate: boolean = false,
                public forceLoadStores: boolean = true) {
    }

    update(forceLoadApp: boolean, forceLoadMenu: boolean, forceInitiate: boolean, forceLoadStores: boolean): ReloadRequest {
        this.forceLoadApp = this.forceLoadApp || forceLoadApp;
        this.forceLoadMenu = this.forceLoadMenu || forceLoadMenu;
        this.forceInitiate = this.forceInitiate || forceInitiate;
        this.forceLoadStores = this.forceLoadStores || forceLoadStores;
        this.requestCount++;
        return this;
    }
}

@Component({
    selector: "woa-ordering-page",
    templateUrl: "./ordering-page.component.html",
    styleUrls: ["./ordering-page.component.scss", "../ui-elements/image/background-image.scss"],
    providers: [{provide: Page, useExisting: OrderingPageComponent}],
    imports: [
        BackgroundImageDirective,
        ButtonComponent,
        ChewzieAttributionComponent,
        ContentMessageComponent,
        DesktopHomePageLayoutComponent,
        FeatureButtonPanelComponent,
        FooterMessageComponent,
        FullPageLayoutComponent,
        GroupOrderSummaryIconComponent,
        IconButtonComponent,
        LegalPanelComponent,
        LinkComponent,
        LiteralPipe,
        MenuCategorySelectionPanelComponent,
        MenuItemSelectionPanelComponent,
        MenuSubcategoryAnchorPanelComponent,
        NgClass,
        OrderEditPanelComponent,
        OrderErrorsPanelComponent,
        OrderHeadingPanelComponent,
        OrderingContextPanelComponent,
        OrderSummaryIconComponent,
        OrderTotalsPanelComponent,
        PageFooterControlDirective,
        PageHeaderImageComponent,
        PromoCarouselComponent,
        QuickPayPanelComponent,
        ResponsiveDirective,
        SelectorControlComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderingPageComponent extends Page implements OnInit, OnDestroy {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public paymentModel: PaymentModel = inject(PaymentModel);
    public menuModel: MenuModel = inject(MenuModel);
    public promoModel: PromoModel = inject(PromoModel);
    public groupOrderModel: GroupOrderModel = inject(GroupOrderModel);
    public appDeviceService: AppDeviceService = inject(AppDeviceService);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private destroyRef: DestroyRef = inject(DestroyRef);
    private notificationService: NotificationService = inject(NotificationService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private loadingService: LoadingService = inject(LoadingService);
    private element: ElementRef = inject(ElementRef);
    private authService: AuthService = inject(AuthService);
    private dealModel: DealModel = inject(DealModel);
    private identityModel: IdentityModel = inject(IdentityModel);

    PageAlignment = PageAlignment;
    MessageLevel = MessageLevel;
    IconButtonType = IconButtonType;
    OrderInitiationStatus = OrderInitiationStatus;
    LoadingStatus = LoadingStatus;
    MessageLayout = MessageLayout;

    constructor() {
        super()
        effect(() => {
            if (this.configurationModel.app()) {
                this.refreshScrolling();
            }
        });

        effectOnSignalSet(this.orderingContextModel.orderType, () => this.requestReload("order type changed", false, true, false, false));

        effect(async () => {
            if (this.onLoadPromptCheckPending() && this.sessionModel.initialLoadStatus() !== LoadingStatus.LOADING) {
                this.onLoadPromptCheckPending.set(false);
                return this.checkOnLoadPrompts("Initial load complete", this.orderModel.canReorderOnLoad);
            }
        });

        effect(() => {
            if (this.sessionModel.orderingPhase() === OrderingPhase.ORDERING) {
                this.requestReload("returned to ordering", false, false, true, true);
            }
        });

        effect(() => {
            if (this.orderingContextModel.evaluatingExpectedCompletionChange() === false) {
                this.orderModel.requestOrderInitiation("ordering page - time change");
            }
        });

        effect(() => {
            this.orderModel.orderInitiationStatus();
            return untracked(() => this.orderInitiationStatusChanged());
        });

        effect(async () => {
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_INVALID) {
                this.requestReload("order invalid on submission", false, true, false, false);
                return;
            }
            if (this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.ORDER_CANCELLED) {
                return this.orderModel.completeOrder();
            }
        });

        effectOnSignalSet(this.responsiveService.mode, () => untracked(() => {
            if (this.responsiveService.mobile() && !this.viewingMenuItems() && this.menuModel.selectedCategory() && !this.menuModel.selectedCategory().id) {
                this.menuModel.deactivatePromotedCategory();
            }
            this.refreshScrolling()
        }));

        effect(() => {
            if (this.responsiveService.windowHeight()) {
                this.refreshScrolling();
            }
        });

        effect(() => {
            if (!this.orderModel.orderHasItems()) {
                this.viewingOrder.set(false);
            }
        })
    }

    /* ================
        PAGE LIFECYCLE
       ================ */

    ngOnInit(): void {

        onEach(this.identityModel.loginRevoked$, () => this.requestReload("login revoked", true, true, false, true), this.destroyRef);
        onEach(this.identityModel.loginComplete$, () => this.requestReload("login complete", true, true, false, true), this.destroyRef);

        onEach(this.orderingContextModel.storeDetailsChanged$, () => this.requestReload("store details changed", true, true, false, false), this.destroyRef);

        onEach(this.orderModel.requestOrderItem$, async (menuItemSelection: MenuItemSelection) => this.orderItem(menuItemSelection, false), this.destroyRef);

        // Set up reorder timer with automatic cancellation
        onEach(this.reorderTimer$.pipe(
            switchMap((delay: number) => timer(delay))
        ), async () => {
            if (this.sessionModel.orderingPhase() === OrderingPhase.ORDERING && this.orderModel.canPromptForReorder) {
                return this.openReorderPage();
            }
            this.orderModel.canReorderOnLoad = true;
        }, this.destroyRef);

        onEach(this.menuModel.categorySelected$, (selection: MenuCategorySelection) => {
            if (!selection.category) {
                this.viewingMenuItems.set(false);
            } else if (this.responsiveService.mobile() && (this.viewingMenuItems() || !selection.reselected)) {
                void this.viewCategory();
            }
        }, this.destroyRef);

        onEach(this.dealModel.dealStatusChanged$, () => this.orderDealProducts(), this.destroyRef);

        this.requestReload("init", false, false, false, true);
        this.checkReorder();
    }

    override onActive(): void {
        this.sessionModel.orderingPhase.set(OrderingPhase.ORDERING);
    }

    ngOnDestroy(): void {
        this.menuModel.unloadMenu();
    }

    /* ========================
        LOAD/RELOAD MANAGEMENT
       ======================== */

    public readonly loadingStatus: WritableSignal<LoadingStatus> = signal<LoadingStatus>(undefined);
    private onLoadPromptCheckPending: WritableSignal<boolean> = signal<boolean>(false);

    public readonly isLoaded: Signal<boolean> = computed<boolean>(() =>
        this.loadingStatus() === LoadingStatus.LOADED || this.loadingStatus() === LoadingStatus.RELOADING);

    private reloadRequested$: Subject<void> = new Subject<void>();
    private reloadSubscriptionInitialized: boolean = false;
    private latestReloadRequest: ReloadRequest;

    /**
     * Reload requests are debounced because multiple events can require a reload,
     * and these events may be triggered by a single action (such as store and order type selection).
     * Debouncing allows the reload logic to be decoupled from the event triggers, while avoiding redundant lookups and race conditions.
     * @param reason
     * @param forceLoadApp
     * @param forceLoadMenu
     * @param forceInitiate
     * @param forceLoadStores
     */
    public requestReload(reason: string, forceLoadApp: boolean, forceLoadMenu: boolean, forceInitiate: boolean, forceLoadStores: boolean): void {
        console.log(Date.now(), "Ordering page reload requested:", forceLoadApp, forceInitiate, forceLoadStores, reason);

        // Record the reload request. Later requests during the debounce-period are merged.
        this.latestReloadRequest = this.latestReloadRequest ? this.latestReloadRequest.update(forceLoadApp, forceLoadMenu, forceInitiate, forceLoadStores) : new ReloadRequest(forceLoadApp, forceLoadMenu, forceInitiate, forceLoadStores);

        // Subscribe to reload requests on the first request
        if (!this.reloadSubscriptionInitialized) {
            this.reloadSubscriptionInitialized = true;
            onEach(this.reloadRequested$.pipe(
                debounceTime(500)
            ), async (): Promise<void> => {

                // Defer concurrent reload attempts by re-requesting.
                // As requests are debounced, any further reloads triggered by actions DURING reload will effectively be batched.
                if (this.loadingStatus() === LoadingStatus.LOADING || this.loadingStatus() === LoadingStatus.RELOADING) {
                    console.log(Date.now(), "Deferring concurrent ordering page reload");
                    this.latestReloadRequest.deferred = true;
                    setTimeout(() => this.reloadRequested$.next(), 500);
                    return;
                }

                // Perform the merged reload request
                const request: ReloadRequest = this.latestReloadRequest;
                this.latestReloadRequest = undefined;
                return this.reload(request);
            }, this.destroyRef);
        }

        // Queue the request to the debouncer
        this.reloadRequested$.next();
    }

    private async reload(request: ReloadRequest): Promise<void> {
        if (this.sessionModel.orderingPhase() !== OrderingPhase.ORDERING) {
            console.log(Date.now(), "Not performing ordering page reload for " + request.requestCount + " merged request(s) - not in ordering phase");
            return;
        }

        console.log(Date.now(), "Performing " + (request.deferred ? "deferred " : "") + "ordering page reload for " + request.requestCount + " merged request(s)");

        let menuLoadRequired: boolean = request.forceLoadMenu || !this.menuModel.menuLoaded() || this.orderModel.orderTypeChangedSinceLastInitiation();
        let canReorder: boolean = false;

        try {
            this.loadingStatus.set(this.loadingStatus() === LoadingStatus.LOADED ? LoadingStatus.RELOADING : LoadingStatus.LOADING);

            await this.loadingService.performWithSpinner(async () => {
                await this.configurationModel.loadApp(request.forceLoadApp);
                await this.appDeviceService.refreshAppDevice("ordering page - reload");

                // These can be done in parallel
                await Promise.all([
                    this.orderingContextModel.loadStores(request.forceLoadApp && request.forceLoadStores),
                    this.orderModel.verifyPendingOrders()
                ]);

                // Member summary and customer payment details require stores to have been loaded as this may select the preselected store.
                await Promise.all([
                    this.identityModel.loadMemberSummary(),
                    this.paymentModel.loadCustomerPaymentDetails("ordering page - reload")
                ]);

                // The menu must be loaded after app and stores.
                // Don't load the menu if there are later reload requests pending.
                if (menuLoadRequired) {
                    if (this.latestReloadRequest) {
                        menuLoadRequired = false;
                        this.latestReloadRequest.forceLoadMenu = true;
                    } else {
                        await this.orderingContextModel.refreshActiveSchedules("ordering page - reload");
                        await this.menuModel.loadMenu("ordering page - reload");
                    }
                }

                this.filterPromotionalCards();

                // Don't initiate the order if there are subsequent reload requests pending.
                if (request.forceInitiate || menuLoadRequired) {
                    if (this.latestReloadRequest) {
                        this.latestReloadRequest.forceInitiate = true;
                    } else {
                        this.orderModel.requestOrderInitiation("ordering page - reload", true);
                    }
                }

                // Note - canRestoreOnLoad and canReorderOnLoad must be checked before the potential return cases which follow,
                // because these are single-shot methods which only return true once until reset.
                // If the return cases occur, we don't want to try the onLoad cases again.
                const canRestore: boolean = this.orderModel.canRestoreOnLoad;
                canReorder = this.orderModel.canReorderOnLoad;

                if (await this.orderPreselectedDealCode()) {
                    return;
                }

                if (canRestore) {
                    if (await this.orderModel.restoreOrder()) {
                        // We have restored order items, so reorder is no longer an option
                        return;
                    }
                    if (!canReorder) {
                        // We may have restored just the store and order type, so we can check again whether reorder is an option
                        canReorder = this.orderModel.canReorderOnLoad;
                    }
                }
            }, menuLoadRequired ? 10000 : 0, "woa.message.order.loading");

            this.loadingStatus.set(LoadingStatus.LOADED);
        } catch (error) {
            // Don't report an error if there is no login (including a guest login).
            // This can happen if the user login is revoked on the back-end. It should resolve and refresh once the guest login is re-established.
            console.error("Ordering page reload failed", error);
            if (this.authService.isLoggedIn()) {
                this.loadingStatus.set(LoadingStatus.FAILED);
            } else {
                console.log("Ignoring reload error as there is no login");
            }
        } finally {
            this.refreshScrolling();
            this.sessionModel.notifyInitialLoadStatus(LoadingStatus.LOADED);

            // Display of prompts is deferred until after loading is complete
            await this.checkOnLoadPrompts("Reload", canReorder);
        }
    }

    /**
     * Called on completion of page load and order initiation.
     * Prompts as necessary for verified orders, group order participation, and reorder round.
     * @param reason
     * @param checkReorder
     */
    private async checkOnLoadPrompts(reason: string, checkReorder: boolean): Promise<void> {
        console.log("Checking on-load prompts:", reason, checkReorder);
        if (this.sessionModel.initialLoadStatus() === LoadingStatus.LOADING) {
            this.onLoadPromptCheckPending.set(true);
            return;
        }

        if (this.loadingStatus() !== LoadingStatus.LOADED || this.sessionModel.orderingPhase() !== OrderingPhase.ORDERING || !this.navigationService.pageIsActive(this)) {
            return;
        }

        await this.orderModel.notifyVerifiedOrders();

        await this.groupOrderModel.promptForGroupOrderParticipation();

        if (checkReorder) {
            if (this.orderModel.canPromptForReorder) {
                return this.openReorderPage();
            }
            this.orderModel.canReorderOnLoad = true;
        }
    }

    /* ============
        PAGE STATE
       ============ */

    public readonly showMobileNavMenuIcon: Signal<boolean> = computed<boolean>(() =>
        !this.viewingOrder() && !this.viewingMenuItems());

    public readonly showOrderingContextPanel: Signal<boolean> = computed<boolean>(() =>
        this.responsiveService.desktop() || this.viewingOrder() || !this.viewingMenuItems());

    public readonly showFeatureButtons: Signal<boolean> = computed<boolean>(() =>
        this.responsiveService.desktop() || this.viewingOrder() || !this.viewingMenuItems());

    public readonly showMobileFooter: Signal<boolean> = computed<boolean>(() =>
        !this.viewingOrder() || (this.loadingStatus() === LoadingStatus.LOADED && this.orderModel.orderInitiationStatus() !== OrderInitiationStatus.FAILED));

    public readonly viewingMobileView: Signal<boolean> = computed<boolean>(() =>
        this.responsiveService.mobile() && (this.viewingOrder() || this.viewingMenuItems()));

    /* ===================
        CAROUSEL CONTENTS
       =================== */

    private static readonly MAX_PROMOTED_DEALS: number = 5;
    private static readonly MAX_PROMOTED_CARD_PROMOS: number = 5;
    private static readonly TEST_MULTIPLE_CARDS: boolean = false;
    private static readonly TEST_ZERO_CARDS: boolean = false;

    // Based on a minimum slide height of 162 px, 15 px gap and margins, and a slide aspect ratio of 16:9
    private static readonly MIN_SCREEN_WIDTH_FOR_TWO_SLIDES: number = 621;

    private readonly allAvailableDeals: WritableSignal<DealData[]> = signal<DealData[]>([]);
    private readonly allUntriggeredCardPromos: WritableSignal<AppPromoData[]> = signal<AppPromoData[]>([]);


    /** -----------------------------
     * The following computed signals, in conjunction with filterPromotionalCards(),
     * find the list of card deals and promos suppressed or triggered by the currently ordered items.
     *
     * Rules for picking triggered card promos:
     * 1. The promo must be triggered and configured to "showAppPromoCard";
     * 2. The promo must be triggered by a product which has been ordered;
     * 3. If the promo has promoted products:
     *      a) One or more promoted products must be both:
     *          i)  not on the order; and
     *          ii) on the menu.
     *
     * Additional filtering will be done in the promo-card component to filter out promoted sellables which are already on the order.
     *
     * Previously, only explicitly triggered card promos were shown after items were ordered (as detailed above).
     * Now, individual promos may be configured to NOT be suppressed after ordering products,
     * or optionally only suppressed if a promoted product has been ordered (as for the popups option).
     * These unsuppressed promos are included in the list (taken from the promotedCardPromos array).
     */

    public readonly promotedDeals: Signal<DealData[]> = computed<DealData[]>(() => {
        const deals: DealData[] = OrderingPageComponent.TEST_ZERO_CARDS ? [] :
            this.allAvailableDeals()
                .filter((deal: DealData) => !deal.isTicket && deal.featureOnAppMenu)
                .slice(0, OrderingPageComponent.MAX_PROMOTED_DEALS);
        return OrderingPageComponent.TEST_MULTIPLE_CARDS ?
            deals.reduce((res: DealData[], current: DealData) => res.concat(Array(9).fill(current)), []) : deals;
    });

    public readonly promotedCardPromos: Signal<AppPromoData[]> = computed<AppPromoData[]>(() => {
        const promos: AppPromoData[] = OrderingPageComponent.TEST_ZERO_CARDS ? [] :
            this.allUntriggeredCardPromos()
                .filter((promo: AppPromoData) => promo.featureOnAppMenu)
                .slice(0, OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS);
        return OrderingPageComponent.TEST_MULTIPLE_CARDS ?
            promos.reduce((res: AppPromoData[], current: AppPromoData) => res.concat(Array(9).fill(current)), []) : promos;
    });

    public readonly triggeredAndUnsuppressedCardPromos: Signal<AppPromoData[]> = computed<AppPromoData[]>(() =>
        !this.orderModel.orderHasItems() ? [] :
            (this.identityModel.currentMember()?.promos.filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                this.promoModel.cardPromoIsTriggered()(promo)) || [])
                .concat(this.promotedCardPromos().filter((promo: AppPromoData) =>
                    !this.promoModel.cardPromoIsSuppressed()(promo)))
                .slice(0, OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS));

    public readonly morePromotionalCards: Signal<boolean> = computed<boolean>(() =>
        OrderingPageComponent.TEST_MULTIPLE_CARDS ? true :
            this.allAvailableDeals().length > OrderingPageComponent.MAX_PROMOTED_DEALS ||
            this.allUntriggeredCardPromos().length > OrderingPageComponent.MAX_PROMOTED_CARD_PROMOS);

    /* ---------------------------------- */

    public readonly showCarousel: Signal<boolean> = computed<boolean>(() => {
        if (this.viewingMobileView() || !this.isLoaded() || this.configurationModel.isWaiterActor()) {
            return false;
        }
        if (this.identityModel.promoteMemberLogin()) {
            return true;
        }
        if ((this.responsiveService.desktop() && !this.configurationModel.app()?.showDesktopMenuPromos) || (this.responsiveService.mobile() && !this.configurationModel.app()?.showMobileMenuPromos)) {
            return false;
        }
        if (this.promotedDeals().length > 0) {
            return true;
        }
        return this.orderModel.personalOrderStarted() ? this.triggeredAndUnsuppressedCardPromos().length > 0 : this.promotedCardPromos().length > 0;
    });

    public readonly showMobileHeaderImage: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor() && !this.viewingOrder() && !this.viewingMenuItems() && !this.showCarousel() && !!this.orderingContextModel.headerImage());

    public readonly nrVisibleSlides: Signal<number> = computed<number>(() => {
        const mode: PageMode = this.responsiveService.calculateMode()(this.responsiveService.calculateAvailableWidth()(this.element.nativeElement, this.desktopHomePage() ? "desktop" : undefined));
        return mode == PageMode.WIDEST_DESKTOP ? 6
            : mode == PageMode.WIDER_DESKTOP ? 5
                : mode == PageMode.WIDE_DESKTOP ? 4
                    : mode == PageMode.BASIC_DESKTOP ? 3
                        : mode == PageMode.NARROW_DESKTOP ? 2
                            : mode == PageMode.MOBILE && window.innerWidth >= OrderingPageComponent.MIN_SCREEN_WIDTH_FOR_TWO_SLIDES ? 2
                                : 1;
    });

    private filterPromotionalCards(): void {
        if (!this.identityModel.currentMember()) {
            return;
        }
        this.allAvailableDeals.set(this.identityModel.currentMember().deals
            .filter((deal: DealData) => deal.isAvailableForOrderType(this.orderingContextModel.orderType()) && !this.dealModel.dealIsSuppressed()(deal)));

        this.allUntriggeredCardPromos.set(this.identityModel.currentMember().promos
            .filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                !promo.triggered &&
                promo.showAppPromoCard &&
                (!promo.promotedSellables.length || this.promoModel.canPromoteSellablesAsCard()(promo, true))));
    }

    /* ================
        PROMO HANDLING
       ================ */

    public readonly presentingPopupPromo: WritableSignal<boolean> = signal<boolean>(false);

    public async promoActioned(promo: AppPromoData, promoAction: AppPromoAction, menuItem: MenuItemData, orderSimpleProductsDirectly: boolean = false, ignoreSizes: boolean = false): Promise<void> {
        if (promoAction === AppPromoAction.ORDER_PRODUCT) {
            return this.orderItem(new MenuItemSelection(menuItem, undefined, orderSimpleProductsDirectly, ignoreSizes));
        }
        if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
            await this.orderingContextModel.untilExpectedCompletionEvaluationComplete();
            this.promoModel.activatedPromo.set(promo);
        }
    }

    /**
     * Presents any necessary triggered popup promo.
     * @return true if a promo is actioned, false otherwise.
     */
    private async presentTriggeredPopupPromo(): Promise<boolean> {
        if (!this.reordering && this.sessionModel.orderingPhase() === OrderingPhase.ORDERING && this.identityModel.currentMember() && this.navigationService.pageIsActive(this)) {
            const promoAction: AppPromoAction = await this.presentPopupPromo(this.promoModel.selectTriggeredPopupWhenOrderedPromo(this.identityModel.currentMember().promos));

            // Go no further if the popup promo is actioned - order will be re-initiated if a new product is ordered, or if the user requested to show promoted products, any progress action is cancelled.
            if (promoAction === AppPromoAction.ORDER_PRODUCT) {
                return true;
            }
            if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
                this.pendingCheckoutAction.set(undefined);
                return true;
            }
        }
        return false;
    }

    /**
     * Presents any necessary checkout popup promo.
     * If an order-product action is accepted, the post-initiation action is set to resume checkout after the product is ordered, and the order is re-initiated.
     * @return true if a promo is actioned, requiring checkout to be stopped or deferred, false otherwise.
     */
    private async presentCheckoutPopupPromo(): Promise<boolean> {
        if (this.identityModel.currentMember()) {
            const promoAction: AppPromoAction = await this.presentPopupPromo(this.promoModel.selectCheckoutPopupPromo());
            if (promoAction === AppPromoAction.ORDER_PRODUCT) {
                this.pendingCheckoutAction.set(null); // proceed to view-order or payment page
                return true;
            }
            if (promoAction === AppPromoAction.SHOW_PRODUCTS) {
                return true;
            }
        }
        return false;
    }

    /**
     * Pops up the given promo and awaits the result.
     * Returns a promise of a boolean indicating whether a product has been ordered from the popup.
     * @param selection
     * @return the promo action, or undefined if the promo is not actioned
     */
    private async presentPopupPromo(selection: {
        promo: AppPromoData,
        menuItem?: MenuItemData
    }): Promise<AppPromoAction> {
        if (!selection || this.presentingPopupPromo()) {
            return undefined;
        }

        try {
            this.presentingPopupPromo.set(true);
            this.loadingService.dismissSpinner();

            const actioned: boolean = await this.navigationService.createPage("woa-promo-page", selection);
            if (selection.menuItem && actioned) {
                void this.promoActioned(selection.promo, selection.promo.popupAction, selection.menuItem, true, true);
                return selection.promo.popupAction;
            }
            return undefined;
        } finally {
            this.presentingPopupPromo.set(false);
        }
    }

    /* ===================
        MENU PRESENTATION
       =================== */

    public anchorPanelER: Signal<ElementRef | undefined> = viewChild("anchorPanel", {read: ElementRef});
    private desktopHomePage: Signal<DesktopHomePageLayoutComponent | undefined> = viewChild("desktopHomePage");

    public readonly viewingMenuItems: WritableSignal<boolean> = signal<boolean>(false);

    public readonly showMenu: Signal<boolean> = computed<boolean>(() => this.isLoaded() && !!this.menuModel.currentMenu());

    public readonly waitTimeNotice: Signal<string> = computed<string>(() =>
        !this.orderingContextModel.showWaitTimeNotices() || (this.responsiveService.mobile() && this.viewingOrder()) ? undefined
            : this.responsiveService.mobile() && !this.viewingMenuItems() ? (this.orderingContextModel.showVenueWaitTimeNotice() ? "woa.message.order.venueWaitTimeNotice" : undefined)
                : this.orderingContextModel.showVenueWaitTimeNotice() && this.orderingContextModel.venueWaitTime() > this.menuModel.selectedCategoryWaitTime() ? "woa.message.order.categoryVenueWaitTimeNotice"
                    : this.menuModel.selectedCategoryWaitTime() ? "woa.message.order.categoryWaitTimeNotice"
                        : undefined);

    public async viewCategory(): Promise<void> {
        if (this.responsiveService.mobile() && this.pageLayout() && this.pageLayout() instanceof FullPageLayoutComponent) {
            (this.pageLayout() as FullPageLayoutComponent).pushScrollPosition();
            await (this.pageLayout() as FullPageLayoutComponent).fadeOutAndIn();
            this.viewingMenuItems.set(true);

            // Create a navigation view for the category items.
            // The promise resolves when the view is dismissed, but will immediately resolve as false if the view already exists on this page.
            if (await this.navigationService.createView("Category View", this)) {
                if (this.viewingMenuItems()) {
                    if (this.responsiveService.mobile() && this.pageLayout() && this.pageLayout() instanceof FullPageLayoutComponent) {
                        await (this.pageLayout() as FullPageLayoutComponent).fadeOutAndIn();
                        this.viewingMenuItems.set(false);
                        (this.pageLayout() as FullPageLayoutComponent).popScrollPosition();
                        if (this.menuModel.selectedCategory() && !this.menuModel.selectedCategory().id) {
                            this.menuModel.deactivatePromotedCategory();
                        }
                    } else {
                        this.viewingMenuItems.set(false);
                    }
                }
            }
        }
    }

    public refreshScrolling(): void {
        setTimeout(() => {
            if (this.responsiveService.desktop() && this.desktopHomePage()) {
                this.desktopHomePage()!.positionSidebar();
            }
        });
    }

    public async searchMenu(): Promise<void> {
        return this.navigationService.createPage("woa-menu-search-page", undefined, {alignment: PageAlignment.TOP}, false, true);
    }

    /* ==================
        ORDER MANAGEMENT
       ================== */

    private static readonly SHOW_ORDER_SUMMARY_ICON: boolean = false;

    public readonly showOrderSummaryIcon: Signal<boolean> = computed<boolean>(() =>
        OrderingPageComponent.SHOW_ORDER_SUMMARY_ICON && this.isLoaded() && (this.orderModel.orderHasItems() || this.dealModel.appliedDeals().length > 0));

    /**
     *
     * @param menuItemSelection
     * @param fromMenu If not ordering from the menu, out of stock menu items are included, and the specified menu item is not prioritised in the search
     */
    public async orderItem(menuItemSelection: MenuItemSelection, fromMenu: boolean = true): Promise<void> {
        let itemOrdered: boolean = false;
        try {

            // If the menu is reloaded due to store/table selection after initially selecting the item, it may become unavailable (store/RSA filtering is done on the server).
            if (!this.menuModel.menuContainsItem()(menuItemSelection.menuItem) || !this.menuModel.sellableIsAvailable()(menuItemSelection.menuItem.sellable.sellableId)) {
                await this.notificationService.displayAlert(null, "woa.message.order.productNotAvailableAtStore", {item: this.menuModel.getMenuItemDisplayName(menuItemSelection.menuItem)});
                return;
            }

            // A product can be added to the menu under multiple menu items, and different details may be specified for each.
            // As such, we want to make sure the menu item used to determine how the product is ordered and ultimately placed in the order for schedule validation
            // reflects a consistent set of details. Such details include available schedules, out-of-stock, product image, and size group regardless of which item the user selected.
            const mergedMenuItem: MenuItemData = this.menuModel.findMergedItemForSellable()({
                sellableId: menuItemSelection.menuItem.sellable.sellableId,
                orderType: this.orderingContextModel.orderType(),
                selectedMenuItem: fromMenu ? menuItemSelection.menuItem : undefined,
                allowOutOfStock: !fromMenu
            });

            if (!menuItemSelection.category?.informational && !menuItemSelection.menuItem.informational) {
                if (!this.menuModel.itemIsAvailableForOrderType()(menuItemSelection.menuItem)) {
                    await this.notificationService.displayAlert(null,
                        !(this.orderingContextModel.singleStoreLocation() && this.orderingContextModel.orderType()) ? "woa.message.order.productNotAvailableAtStore"
                            : this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD ? "woa.message.order.productNotAvailableOrderAhead"
                                : this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY ? "woa.message.order.productNotAvailableOrderDelivery"
                                    : "woa.message.order.productNotAvailableOrderAtTable", {item: this.menuModel.getMenuItemDisplayName(menuItemSelection.menuItem)});
                    return;
                }

                const orderDirectly: boolean = menuItemSelection.orderSimpleProductsDirectly && this.menuModel.isSimpleProductItem()(mergedMenuItem, menuItemSelection.ignoreSizes, menuItemSelection.requiredExtrasOnly);

                if (orderDirectly ? this.menuModel.itemOutOfStock()(mergedMenuItem) : this.menuModel.allSizesOutOfStock()(mergedMenuItem)) {
                    await this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getMenuItemDisplayName(menuItemSelection.menuItem)});
                    return;
                }
                if (orderDirectly ? this.orderModel.menuItemAtLimit()(mergedMenuItem) : this.orderModel.allSizesAtLimit()(mergedMenuItem)) {
                    await this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getMenuItemDisplayName(menuItemSelection.menuItem)});
                    return;
                }
                await this.orderingContextModel.untilExpectedCompletionEvaluationComplete();
                if (!this.menuModel.itemIsAvailableOnSchedules()(mergedMenuItem)) {
                    await this.notificationService.displayAlert(null, "woa.message.order.productNotAvailableAtSchedules", {item: this.menuModel.getMenuItemDisplayName(menuItemSelection.menuItem)});
                    return;
                }

                // Both store and order type must be selected before an item can be added to the order, because item availability may differ between stores and order types.
                // For waiters, we enforce table selection at this point as well, since store and table selection are handled simultaneously in that mode.
                if (this.configurationModel.isWaiterActor() && this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE && !this.orderingContextModel.isConfirmedTableOrdering()) {
                    return this.orderingContextModel.selectAndApplyContext(new OrderingContext()
                        .withIntention(OrderingContextIntention.ORDER)
                        .withSelectedOrderType(OrderType.ORDER_AT_TABLE)
                        .withSelectedMenuItem(new MenuItemSelection(mergedMenuItem, menuItemSelection.category, menuItemSelection.orderSimpleProductsDirectly, menuItemSelection.ignoreSizes, menuItemSelection.requiredExtrasOnly)));
                }
                if (!this.orderingContextModel.currentStore() || !this.orderingContextModel.orderType()) {
                    return this.orderingContextModel.selectAndApplyStoreAndOrderType(new OrderingContext()
                        .withIntention(OrderingContextIntention.ORDER)
                        .withDefaultOrderType(this.orderingContextModel.orderType())
                        .withSelectedMenuItem(new MenuItemSelection(mergedMenuItem, menuItemSelection.category, menuItemSelection.orderSimpleProductsDirectly, menuItemSelection.ignoreSizes, menuItemSelection.requiredExtrasOnly))
                        .withSelectedTable(this.orderingContextModel.preselectedTableSelection()));
                }

                if (this.orderingContextModel.currentStore().rsaMinimumAge && mergedMenuItem.sellable.applyRsa && !await this.orderModel.verifyRsa()) {
                    return;
                }

                if (menuItemSelection.orderSimpleProductsDirectly && this.menuModel.isSimpleProductItem()(mergedMenuItem, menuItemSelection.ignoreSizes, menuItemSelection.requiredExtrasOnly)) {
                    const orderItem: OrderItem = this.orderModel.createOrderItem(mergedMenuItem, (mergedMenuItem.sellable.minWebOrderQty || 1));
                    this.orderModel.addOrderItem(orderItem);
                    this.notificationService.displayFooterMessage(new MessageDetails(
                        MessageLevel.SUCCESS, "woa.message.order.itemOrdered", {"item": this.menuModel.getMenuItemDisplayName(mergedMenuItem)}, "check-circle", true,
                        "woa.button.common.edit", () => this.orderModel.editOrderItem(orderItem, true)), true);
                    void this.analyticsService.trackItemAddedToOrder(orderItem, mergedMenuItem.promotedFeatureTag);
                    itemOrdered = true;
                    menuItemSelection.simpleProductAdded();
                    return;
                }
            }

            void this.analyticsService.trackViewItem(menuItemSelection.menuItem);
            const orderItem: OrderItem = await this.navigationService.createPage("woa-order-item-edit-page", {
                menuItem: mergedMenuItem,
                category: menuItemSelection.category
            });
            itemOrdered = !!orderItem;
        } finally {
            if (!itemOrdered) {
                // Check for any triggered promos due to items previously added
                setTimeout(() => this.orderInitiationStatusChanged());
            }
        }
    }

    public async orderDeal(deal: DealData): Promise<void> {
        await this.orderingContextModel.untilExpectedCompletionEvaluationComplete();
        const result: DealResult = await this.dealModel.orderDeal(deal);
        if (result.resultCode === DealResultCode.UNAVAILABLE) {
            await this.notificationService.displayAlert(null, result.msg);
        }
    }

    private async orderPreselectedDealCode(): Promise<boolean> {
        const dealCode: string = this.orderingContextModel.getAndClearPreselectedDealCode();
        if (!dealCode) {
            return false;
        }
        const result: DealResult = await this.dealModel.orderDealByDealCode(dealCode);
        if (result.resultCode === DealResultCode.UNAVAILABLE) {
            await this.notificationService.displayAlert(null, "woa.message.order.dealNotValid");
        }
        return result.resultCode === DealResultCode.IMMEDIATE;
    }

    private async orderDealProducts(): Promise<void> {
        if (this.sessionModel.orderingPhase() !== OrderingPhase.ORDERING || this.dealModel.pendingDealStatus() === PendingDealStatus.ORDERING_PRODUCTS) {
            return;
        }

        // Defer if still loading
        if (this.loadingStatus() === LoadingStatus.RELOADING && this.dealModel.pendingDealStatus() === PendingDealStatus.PRODUCT_ORDER_REQUIRED) {
            setTimeout(() => this.dealModel.notifyDealStatusChanged(), 500);
            return;
        }

        if (this.loadingStatus() !== LoadingStatus.LOADED) {
            return;
        }

        if (this.dealModel.pendingDealStatus() !== PendingDealStatus.PRODUCT_ORDER_REQUIRED) {
            this.orderModel.requestOrderInitiation("ordering page - order deal products");
            return;
        }

        if (this.dealModel.shouldApplyRsaToDeal()(this.dealModel.pendingDeal()) && !await this.orderModel.verifyRsa()) {
            return;
        }

        let unavailableSchedules: boolean = false;
        let unavailableQuantity: boolean = false;
        let itemsToOrder: OrderItem[] = [];
        let accessibleOutlets: number[] = this.dealModel.pendingDeal().discount.accessibleOutlets || this.dealModel.pendingDeal().mixAndMatch.accessibleOutlets;
        if ((accessibleOutlets && accessibleOutlets.indexOf(this.orderingContextModel.currentStore().outletId) < 0) ||
            !this.dealModel.pendingDeal().products.every((dealProduct: DealProductData) => {
                const menuItem: MenuItemData = this.menuModel.findMergedItemForSellable()({
                    sellableId: dealProduct.sellable.sellableId,
                    orderType: this.orderingContextModel.orderType(),
                    allowOutOfStock: true
                });
                if (!menuItem) {
                    return false;
                }
                if (!this.menuModel.itemIsAvailableOnSchedules()(menuItem)) {
                    unavailableSchedules = true;
                    return false;
                }
                if (this.menuModel.itemOutOfStock()(menuItem)) {
                    unavailableQuantity = true;
                    return false;
                }
                if (this.orderModel.menuItemAtLimit()(menuItem, dealProduct.quantity || 1)) {
                    unavailableQuantity = true;
                    return false;
                }
                for (let index = 0; index < (dealProduct.quantity || 1); index++) {
                    let orderItem: OrderItem = this.orderModel.createOrderItem(menuItem, menuItem.sellable.minWebOrderQty || 1);
                    orderItem.dealIdSignal.set(this.dealModel.pendingDeal().id);
                    itemsToOrder.push(orderItem);
                }
                return true;
            })
        ) {
            this.dealModel.cancelPendingDeal();
            if (unavailableSchedules) {
                void this.notificationService.displayAlert(null, "woa.message.order.dealNotAvailableAtSchedules");
            } else if (unavailableQuantity) {
                void this.notificationService.displayAlert(null, "woa.message.order.dealOutOfStock");
            } else if (this.orderingContextModel.singleStoreLocation() && this.orderingContextModel.orderType()) {
                void this.notificationService.displayAlert(null,
                    this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD ? "woa.message.order.dealNotAvailableOrderAhead"
                        : this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY ? "woa.message.order.dealNotAvailableOrderDelivery"
                            : "woa.message.order.dealNotAvailableOrderAtTable");
            } else {
                void this.notificationService.displayAlert(null, "woa.message.order.dealNotAvailableAtStore");
            }
            return;
        } else if (itemsToOrder.length > 0) {
            this.dealModel.pendingDealStatus.set(PendingDealStatus.ORDERING_PRODUCTS);
            this.dealModel.notifyDealStatusChanged();
            void this.navigationService.createPage("woa-order-item-edit-page", {itemsToOrder: itemsToOrder});
        }
    }

    private async orderInitiationStatusChanged(): Promise<void> {
        if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATED) {
            if (this.sessionModel.initialLoadStatus() === LoadingStatus.LOADED) {
                await this.checkOnLoadPrompts("Order initiated", this.orderModel.canReorderOnLoad);
            }
            if (await this.presentTriggeredPopupPromo()) {
                return;
            }

            // Reinitiate deferred checkout
            if (this.checkoutPending()) {
                const paymentType: PaymentType | null = this.pendingCheckoutAction();
                this.pendingCheckoutAction.set(undefined);
                if (this.navigationService.pageIsActive(this)) {
                    setTimeout(() => {
                        if (paymentType !== null) {
                            void this.quickPay(paymentType);
                        } else if (this.responsiveService.mobile() && !this.viewingOrder()) {
                            void this.viewOrder(true);
                        } else {
                            void this.checkout(true);
                        }
                    });
                }
            }
        } else if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.FAILED) {
            this.pendingCheckoutAction.set(undefined);
        }
        if (!this.reordering && this.sessionModel.orderingPhase() === OrderingPhase.ORDERING) {
            this.filterPromotionalCards();
        }
    }

    /* ====================
        REORDER MANAGEMENT
       ==================== */

    private static readonly REORDER_CHECK_TIMEOUT: number = 60000 * 5;

    private reordering: boolean = false;
    private reorderTimer$: Subject<number> = new Subject<number>();

    /**
     * If an order has been placed, calculate the amount of time before showing the reorder dialogue.
     * If not in a state to prompt, the canReorderOnLoad state is reset to prompt the next time the page is loaded.
     */
    private checkReorder(): void {
        if (this.orderModel.orderPlacementFinished()) {
            let delay: number = OrderingPageComponent.REORDER_CHECK_TIMEOUT - (Date.now() - this.orderModel.orderPlacementFinished());
            if (delay < 0) {
                delay = 0;
            }
            this.reorderTimer$.next(delay);
            this.orderModel.orderPlacementFinished.set(undefined);
        }
    }

    private async openReorderPage(): Promise<void> {
        this.reordering = true;
        const status: ReorderStatus = await this.navigationService.createPage("woa-reorder-page");
        if (!(status && status.quickPay)) {
            this.reordering = false;

            // Handle a group order reinitiation error by opening the group order page to display the error and allow retry
            if (status && (status.groupOrderValidationMessages || status.groupOrderError)) {
                await this.navigationService.createPage("woa-group-order-page", {
                    mode: GroupOrderPageMode.EDIT,
                    reorderStatus: status
                });
            }

            // Check for any triggered promos due to items added in the reorder
            return this.orderInitiationStatusChanged();
        }
    }

    /* ==========
        CHECKOUT
       ========== */

    public readonly viewingOrder: WritableSignal<boolean> = signal<boolean>(false);

    /**
     * How to progress with checkout after order initiation:
     *   undefined = no checkout;
     *   null = proceed to view-order or payment page
     *   otherwise = quick pay with the specified payment type
     */
    private readonly pendingCheckoutAction: WritableSignal<PaymentType | null | undefined> = signal<PaymentType | null | undefined>(undefined);

    public readonly checkoutPending: Signal<boolean> = computed<boolean>(() => this.pendingCheckoutAction() !== undefined);

    public readonly suppressQuickPay: Signal<boolean> = computed<boolean>(() => this.responsiveService.desktop() && !!this.promoModel.availableCheckoutPopupPromos().length);

    public readonly otherPayLabel: Signal<string> = computed<string>(() =>
        this.checkoutPending() ? "woa.message.order.initiating"
            : this.orderingContextModel.fulfilmentDetailsRequired() ? "woa.button.checkout.checkout"
                : this.orderModel.orderTotal() <= 0 ? "woa.button.checkout.placeOrder"
                    : this.paymentModel.showQuickPay() && !this.suppressQuickPay() ? "woa.button.checkout.payOther"
                        : "woa.button.checkout.payNow");

    public readonly otherPayProminence: Signal<ButtonProminence> = computed<ButtonProminence>(() =>
        this.paymentModel.showQuickPay() ? ButtonProminence.LOW : ButtonProminence.HIGH);

    /**
     * For mobile - switches to order view mode.
     * Checkout promos are presented if required.
     * @param deferred
     */
    public async viewOrder(deferred: boolean = false): Promise<void> {
        if (this.checkoutPending()) {
            return;
        }

        if (!await this.confirmWaiterName()) {
            return;
        }

        this.promoModel.notifyCheckoutStarted(deferred);
        if (await this.presentCheckoutPopupPromo()) {
            return;
        }

        await this.sessionModel.checkLiveMode();

        if (this.pageLayout() && this.pageLayout() instanceof FullPageLayoutComponent) {
            (this.pageLayout() as FullPageLayoutComponent).pushScrollPosition();
            await (this.pageLayout() as FullPageLayoutComponent).fadeOutAndIn();
            this.viewingOrder.set(true);

            // Create a navigation view for the order.
            // The promise resolves when the view is dismissed, but will immediately resolve as false if the view already exists on this page.
            if (await this.navigationService.createView("Order View", this)) {
                if (this.viewingOrder()) {
                    if (this.responsiveService.mobile() && this.pageLayout() && this.pageLayout() instanceof FullPageLayoutComponent) {
                        await (this.pageLayout() as FullPageLayoutComponent).fadeOutAndIn();
                        this.viewingOrder.set(false);
                        (this.pageLayout() as FullPageLayoutComponent).popScrollPosition();
                    } else {
                        this.viewingOrder.set(false);
                    }
                }
            }
        }
    }

    /**
     * Progresses to the payment page.
     * For desktop, checkout promos are presented if required.
     * @param deferred
     */
    public async checkout(deferred: boolean = false): Promise<void> {
        if (this.responsiveService.desktop()) {
            this.promoModel.notifyCheckoutStarted(deferred);
        }

        if (!await this.isOrderValidForPayment()) {
            return;
        }

        if (this.paymentModel.quickPayType() === PaymentType.NONE) {
            return this.quickPay(PaymentType.NONE);
        }

        // If group orders are in play, we want to force initiation of the order if it has not been initiated within the last 10 seconds, to pick up the current group orders.
        if (this.orderModel.orderInitiationStatus() !== OrderInitiationStatus.INITIATED ||
            (this.groupOrderModel.canHaveGroupOrder() && new Date().getTime() - this.orderModel.orderInitiatedTime() > 10000)) {
            this.pendingCheckoutAction.set(null); // proceed to view-order or payment page
            this.orderModel.requestOrderInitiation("ordering page - check group orders");
            return;
        }

        if (this.responsiveService.desktop()) {
            if (!await this.confirmWaiterName()) {
                return;
            }
            if (await this.presentCheckoutPopupPromo()) {
                return;
            }
        }

        this.pendingCheckoutAction.set(undefined);

        // For tyro, we can't prompt for tips on the payment page
        if (this.paymentModel.paymentProvider() === IntegratedPaymentProvider.TYRO && !await this.orderingContextModel.promptForTip()) {
            return;
        }

        return this.navigationService.createPage("woa-payment-page");
    }

    /**
     * Ensures that a waiter name has been entered if required
     */
    private async confirmWaiterName(): Promise<boolean> {
        if (!this.configurationModel.requireWaiterName() || this.identityModel.waiterName()) {
            return true;
        }

        const name: string = await this.navigationService.createPage("woa-waiter-name-edit-page");
        if (name && name.trim()) {
            this.identityModel.waiterName.set(name.trim());
            return true;
        }
        return false;
    }

    public async quickPay(paymentType: PaymentType): Promise<void> {
        if (!await this.isOrderValidForPayment(paymentType)) {
            return;
        }

        this.pendingCheckoutAction.set(undefined);
        void this.orderModel.placeOrder(paymentType, this.groupOrderModel.preferredTransferTarget());
    }

    /**
     * Validates that the order is ready for checkout with the (optionally) specified payment type.
     * If the order is valid, but order initiation is still pending, the post-initiation action is set to resume checkout after initiation completes.
     * @return true if the checkout can proceed, or false if it should be stopped or deferred.
     */
    private async isOrderValidForPayment(paymentType?: PaymentType): Promise<boolean> {
        if (this.checkoutPending() || this.notificationService.alertDisplayed()) {
            return false;
        }
        this.pendingCheckoutAction.set(paymentType);

        await this.orderingContextModel.untilExpectedCompletionEvaluationComplete();
        if (!await this.orderModel.validateOrderForPayment(paymentType, true)) {
            await this.analyticsService.trackNotReadyToPlaceOrder();
            this.pendingCheckoutAction.set(undefined);
            return false;
        }

        if (this.orderingContextModel.fulfilmentDetailsRequired()) {
            this.pendingCheckoutAction.set(undefined);
            void this.orderingContextModel.changeFulfilmentDetails();
            return false;
        }

        if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATING ||
            this.orderModel.orderSubmissionStatus() === OrderSubmissionStatus.SUBMITTING) {
            return false;
        }
        this.pendingCheckoutAction.set(undefined);
        return true;
    }
}
